import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Plus } from "lucide-react";
import moment from "moment";

import { PersonsService } from "../../../services/personsService";
import { hasRole, ROLE_NAMES } from "../../../Helpers/roles";
import { sortUsersByName } from "../../../Helpers/sortUsersByName";
import Alert from "../../components/Alert";
import Button from "../../components/Button";
import ESUser from "./ESUser";
import COSSUser from "./COSSUser";
import ExternalCOSSUser from "./ExternalCOSSUser";

const Users = (props) => {
  const { handleInputChange } = props;
  const [esList, setEsList] = useState([]);
  const [cossList, setCossList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const allUsers = props.allUsers || {};
  const authUser = useSelector((state) => state.auth.user);
  const packData = props.values;

  useEffect(() => {
    const users = cloneDeep(allUsers.esusers || []);
    if (users.length === 0 || !props.plannedStartDate || !props.plannedEndDate) return;
    let changed = false;
    if (!users[0].start_date) {
      users[0].start_date = props.plannedStartDate;
      changed = true;
    }
    const finalIndex = users.length - 1;
    if (!users[finalIndex].end_date) {
      users[finalIndex].end_date = props.plannedEndDate;
      changed = true;
    }
    if (changed) handleInputChange("users.esusers", users, { systemSync: true });
  }, [allUsers.esusers, handleInputChange, props.plannedEndDate, props.plannedStartDate]);

  useEffect(() => {
    PersonsService.getUsersByType([3, 4])
      .then((response) => {
        const users = sortUsersByName(response.data);
        setEsList(users.filter((user) => user.id !== authUser.id && hasRole(user, ROLE_NAMES.ENGINEERING_SUPERVISOR)));
        setCossList(users.filter((user) => user.id !== authUser.id && hasRole(user, ROLE_NAMES.COSS)));
      })
      .catch((error) => console.log(error))
      .finally(() => setUsersLoading(false));
  }, [authUser.id]);

  useEffect(() => {
    const existingPackData = cloneDeep(packData);
    const { esusers = [], cossusers = [], cossXusers = [] } = existingPackData.users || {};
    const findUser = (user) => {
      if (!user) return null;
      if (user.user_id) return esusers.find((item) => item.user_id === user.user_id) || cossusers.find((item) => item.user_id === user.user_id) || null;
      if (user.user_reference !== undefined) return cossXusers.find((item) => item.user_reference === user.user_reference && item.name) || null;
      return null;
    };
    const forms = Object.keys(existingPackData.forms || {}).reduce((result, key) => {
      const form = existingPackData.forms[key];
      if (form.trolleys) form.trolleys = form.trolleys.map((trolley) => ({ ...trolley, coss: findUser(trolley.coss), relieving_coss: findUser(trolley.relieving_coss) }));
      if (form.responsible_person) form.responsible_person = findUser(form.responsible_person);
      if (form.es_withdrawing) form.es_withdrawing = findUser(form.es_withdrawing);
      if (!form.es_withdrawing) form.es_withdrawing_phone_no = null;
      if (form.es_placing) form.es_placing = findUser(form.es_placing);
      if (!form.es_placing) form.es_placing_phone_no = null;
      if (form.pses) form.pses = form.pses.map((pse) => ({ ...pse, pse_user: findUser(pse.pse_user) }));
      result[key] = form;
      return result;
    }, {});
    handleInputChange("forms", forms, { systemSync: true });
  // User changes are the trigger; including the derived forms object would make this synchronization self-triggering.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, handleInputChange]);

  const endReorderEs = (result) => {
    if (!result.destination) return;
    const users = Array.from(allUsers.esusers || []);
    const [moved] = users.splice(result.source.index, 1);
    users.splice(result.destination.index, 0, moved);
    props.handleInputChange("users.esusers", users);
  };

  const packDates = props.plannedStartDate && props.plannedEndDate
    ? `${moment(props.plannedStartDate).format("D MMMM YYYY, HH:mm")} to ${moment(props.plannedEndDate).format("D MMMM YYYY, HH:mm")}`
    : "Complete the location step to set the pack dates.";
  const addEngineeringSupervisor = () => {
    const users = allUsers.esusers || [];
    const previousUser = users[users.length - 1];
    const newUser = {
      ...cloneDeep(props.userTemplate),
      start_date: previousUser?.end_date || props.plannedStartDate || "",
      end_date: props.plannedEndDate || "",
    };
    props.handleAddFormRow("users.esusers", newUser);
  };

  return (
    <div className="wsm-pack-users-step">
      <div className="wsm-pack-form__notices">
        <Alert title="Pack dates" type="info">{packDates}</Alert>
        <Alert title="Changing assigned users" type="warning">Removing or changing a user will also remove them from forms where they are currently assigned.</Alert>
        {props.errors?.["data.users"] && <Alert title="Check assigned users" type="error">{props.errors["data.users"][0]}</Alert>}
      </div>

      <section className="wsm-pack-form-section">
        <div className="wsm-pack-form-section__heading">
          <div><h3>Engineering supervisors</h3><p>Assign supervisors and the period each person will cover.</p></div>
          <Button icon={Plus} onClick={addEngineeringSupervisor} size="compact" type="button" variant="outline">Add supervisor</Button>
        </div>
        <DragDropContext onDragEnd={endReorderEs}>
          <Droppable droppableId="droppable-esusers">
            {(provided) => (
              <div className="wsm-pack-form-section__entries" ref={provided.innerRef} {...provided.droppableProps}>
                {(allUsers.esusers || []).map((user, userIndex) => (
                  <Draggable
                    draggableId={`es-user-${userIndex}`}
                    index={userIndex}
                    isDragDisabled={(allUsers.esusers || []).length < 2}
                    key={`es-user-${userIndex}`}
                  >
                    {(dragProvided, snapshot) => (
                      <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <ESUser {...props} allEsUsers={allUsers.esusers || []} dragHandleProps={dragProvided.dragHandleProps} isDragging={snapshot.isDragging} userData={user} userIndex={userIndex} userOptions={esList.filter((candidate) => !(allUsers.cossusers || []).some((selected) => selected.user_id && selected.user_id === candidate.id))} usersLoading={usersLoading} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </section>

      <section className="wsm-pack-form-section">
        <div className="wsm-pack-form-section__heading">
          <div><h3>IWA and COSS users</h3><p>Add people responsible for safe work during this pack.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow("users.cossusers", props.userTemplate)} size="compact" type="button" variant="outline">Add IWA/COSS</Button>
        </div>
        {props.errors?.["data.users.cossusers"] && <Alert type="error">{props.errors["data.users.cossusers"][0]}</Alert>}
        <div className="wsm-pack-form-section__entries">
          {(allUsers.cossusers || []).map((user, userIndex) => <COSSUser {...props} allCossUsers={allUsers.cossusers || []} key={`coss-user-${user.user_id || userIndex}`} userData={user} userIndex={userIndex} userOptions={cossList.filter((candidate) => !(allUsers.esusers || []).some((selected) => selected.user_id && selected.user_id === candidate.id))} usersLoading={usersLoading} />)}
        </div>
        {(allUsers.cossusers || []).length === 0 && <p className="wsm-pack-form-section__empty">No IWA or COSS users have been assigned.</p>}
      </section>

      <section className="wsm-pack-form-section">
        <div className="wsm-pack-form-section__heading">
          <div><h3>External IWA and COSS users</h3><p>Add external users who do not have a Worksite Manager account.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow("users.cossXusers", props.userTemplate)} size="compact" type="button" variant="outline">Add external user</Button>
        </div>
        <div className="wsm-pack-form-section__entries">
          {(allUsers.cossXusers || []).map((user, userIndex) => <ExternalCOSSUser {...props} allExternalCossUsers={allUsers.cossXusers || []} key={`external-user-${user.user_reference || userIndex}`} userData={user} userIndex={userIndex} />)}
        </div>
        {(allUsers.cossXusers || []).length === 0 && <p className="wsm-pack-form-section__empty">No external users have been assigned.</p>}
      </section>
    </div>
  );
};

export default Users;
